import React, { useState, useRef, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  LinearProgress,
  Alert,
  Collapse,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import BackupIcon          from '@mui/icons-material/Backup';
import CloudDownloadIcon   from '@mui/icons-material/CloudDownload';
import StorageIcon         from '@mui/icons-material/Storage';
import UploadFileIcon      from '@mui/icons-material/UploadFile';
import DownloadIcon        from '@mui/icons-material/Download';
import PeopleAltIcon       from '@mui/icons-material/PeopleAlt';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import ErrorIcon           from '@mui/icons-material/Error';
import DoorFrontIcon       from '@mui/icons-material/DoorFront';
import ExpandMoreIcon      from '@mui/icons-material/ExpandMore';
import ExpandLessIcon      from '@mui/icons-material/ExpandLess';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon           from '@mui/icons-material/Close';
import WarningAmberIcon    from '@mui/icons-material/WarningAmber';

import { useAppTheme } from '../../contexts/ThemeContext';
import { fetchApartments } from '../Properties/api';
import API from './Api';

// ─── tiny helpers ────────────────────────────────────────────────────────────

const fmt = (n) => n?.toLocaleString() ?? '0';

// ─── sub-component: per-row result list ──────────────────────────────────────

const ResultRow = ({ result, palette, Colors }) => {
  const [open, setOpen] = useState(false);

  return (
    <Box
      sx={{
        border: `1px solid ${
          !result.success
            ? `${Colors.accent?.red ?? '#ef4444'}40`
            : result.row_type === 'vacant'
            ? `${Colors.accent?.blue ?? '#3b82f6'}40`
            : `${Colors.primary.main}40`
        }`,
        borderRadius: 1.5,
        mb: 0.75,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          cursor: result.success ? 'pointer' : 'default',
          backgroundColor: !result.success
            ? `${Colors.accent?.red ?? '#ef4444'}08`
            : result.row_type === 'vacant'
            ? `${Colors.accent?.blue ?? '#3b82f6'}08`
            : `${Colors.primary.main}08`,
          '&:hover': result.success
            ? result.row_type === 'vacant'
              ? { backgroundColor: `${Colors.accent?.blue ?? '#3b82f6'}14` }
              : { backgroundColor: `${Colors.primary.main}14` }
            : {},
        }}
        onClick={() => result.success && setOpen((p) => !p)}
      >
        {result.success
          ? result.row_type === 'vacant'
            ? <DoorFrontIcon   sx={{ fontSize: 18, color: Colors.accent?.blue ?? '#3b82f6', flexShrink: 0 }} />
            : <CheckCircleIcon sx={{ fontSize: 18, color: Colors.primary.main, flexShrink: 0 }} />
          : <ErrorIcon         sx={{ fontSize: 18, color: Colors.accent?.red ?? '#ef4444', flexShrink: 0 }} />
        }

        <Typography variant="caption" sx={{ color: palette.text.secondary, mr: 0.5, flexShrink: 0 }}>
          Row {result.row}
        </Typography>

        <Typography variant="body2" sx={{ color: palette.text.primary, flex: 1, fontWeight: 500 }}>
          {result.message}
        </Typography>

        {result.success && (
          <IconButton size="small" sx={{ color: palette.text.secondary, p: 0.25 }}>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>

      {/* Detail pills — only for successful rows */}
      <Collapse in={open}>
        <Box sx={{ px: 2, py: 1.25, display: 'flex', flexWrap: 'wrap', gap: 0.75, backgroundColor: palette.background.secondary }}>
          {[
            { label: `Unit: ${result.unit}`,          show: result.unit },
            { label: `Category: ${result.category}`,  show: result.category },
            { label: `ID: ${result.account_id}`,      show: result.account_id },
            { label: `${result.bills_created} bill(s)`, show: result.bills_created > 0 },
            { label: 'Water reading set',              show: result.water_reading_set },
            { label: 'Category created',  show: result.category_created,
              color: Colors.accent?.blue ?? '#3b82f6' },
            { label: 'Unit created',     show: result.unit_action === 'created',
              color: Colors.accent?.blue ?? '#3b82f6' },
            { label: 'Unit renamed',     show: result.unit_action === 'renamed',
              color: Colors.accent?.blue ?? '#3b82f6' },
            { label: 'ID auto-generated', show: result.id_generated,
              color: Colors.accent?.orange ?? '#f59e0b' },
          ]
            .filter((p) => p.show)
            .map((p, i) => (
              <Chip
                key={i}
                label={p.label}
                size="small"
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  bgcolor: `${p.color ?? Colors.primary.main}20`,
                  color:    p.color ?? Colors.primary.main,
                  height: 22,
                }}
              />
            ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

const BackupSettings = ({ defaultSettings = {} }) => {
  const { palette, Colors } = useAppTheme();

  // ── backup state ──────────────────────────────────────────────────────────
  const [backupSettings, setBackupSettings] = useState({
    autoBackup:    defaultSettings.autoBackup    ?? true,
    dataRetention: defaultSettings.dataRetention || '3 years',
  });

  const [backups] = useState([
    { id: 1, date: 'March 15, 2025 - 09:00 AM', type: 'Automatic backup', size: '24.6 MB' },
    { id: 2, date: 'March 8, 2025 - 09:00 AM',  type: 'Automatic backup', size: '24.2 MB' },
    { id: 3, date: 'March 5, 2025 - 02:15 PM',  type: 'Manual backup',    size: '24.1 MB' },
  ]);

  // ── onboarding state ──────────────────────────────────────────────────────
  const [apartments,          setApartments]          = useState([]);
  const [apartmentsLoading,   setApartmentsLoading]   = useState(true);
  const [selectedApartmentId, setSelectedApartmentId] = useState('');
  const [pickedFile,          setPickedFile]          = useState(null);   // File object
  const [uploadPhase, setUploadPhase] = useState('idle');
  // 'idle' | 'validating' | 'preview' | 'uploading' | 'done' | 'error'
  const [previewData,  setPreviewData]  = useState(null);   // dry-run result
  const [uploadResult, setUploadResult] = useState(null);   // final result
  const [errorMsg,     setErrorMsg]     = useState('');
  const [showResults,  setShowResults]  = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);

  const fileInputRef = useRef(null);

  // load apartments on mount
  useEffect(() => {
    setApartmentsLoading(true);
    fetchApartments()
      .then((apartments) => setApartments(apartments))
      .catch((err) => {
        console.error('Failed to load apartments:', err);
        setApartments([]);
      })
      .finally(() => setApartmentsLoading(false));
  }, []);

  // reset file + results when apartment changes
  useEffect(() => {
    setPickedFile(null);
    setUploadPhase('idle');
    setPreviewData(null);
    setUploadResult(null);
    setErrorMsg('');
    setShowResults(false);
  }, [selectedApartmentId]);

  // ── backup handlers ───────────────────────────────────────────────────────
  const handleSettingsChange = (field) => (event) => {
    setBackupSettings({
      ...backupSettings,
      [field]: field === 'autoBackup' ? event.target.checked : event.target.value,
    });
  };
  const handleCreateBackup   = () => console.log('Creating backup…');
  const handleRestoreBackup  = (id) => console.log(`Restoring backup ${id}…`);

  // ── onboarding handlers ───────────────────────────────────────────────────
  const handleTemplateDownload = async () => {
    setTemplateLoading(true);
    try {
      await API.onboarding.downloadTemplate();
    } catch (e) {
      setErrorMsg('Could not download template. Please try again.');
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleFilePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPickedFile(file);
    setUploadPhase('idle');
    setPreviewData(null);
    setUploadResult(null);
    setErrorMsg('');
    setShowResults(false);
  };

  const handleValidate = async () => {
    if (!pickedFile || !selectedApartmentId) return;
    setUploadPhase('validating');
    setErrorMsg('');
    try {
      const result = await API.onboarding.validateFile(pickedFile, selectedApartmentId);
      setPreviewData(result);
      setUploadPhase('preview');
    } catch (e) {
      setErrorMsg(e.message || 'Validation failed.');
      setUploadPhase('error');
    }
  };

  const handleUpload = async () => {
    if (!pickedFile || !selectedApartmentId) return;
    setUploadPhase('uploading');
    setErrorMsg('');
    try {
      const result = await API.onboarding.uploadFile(pickedFile, selectedApartmentId);
      setUploadResult(result);
      setUploadPhase('done');
      setShowResults(true);
    } catch (e) {
      setErrorMsg(e.message || 'Upload failed.');
      setUploadPhase('error');
    }
  };

  const handleReset = () => {
    setPickedFile(null);
    setUploadPhase('idle');
    setPreviewData(null);
    setUploadResult(null);
    setErrorMsg('');
    setShowResults(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── shared card style ─────────────────────────────────────────────────────
  const cardSx = {
    mb: 3,
    backgroundColor: palette.background.tertiary,
    border: `1px solid ${palette.border}`,
    boxShadow: 'none',
  };

  const switchSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: Colors.primary.main },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: Colors.primary.main },
  };

  const selectSx = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: palette.border },
      '&:hover fieldset': { borderColor: Colors.primary.main },
      backgroundColor: palette.background.secondary,
    },
    '& .MuiInputLabel-root': { color: palette.text.secondary },
    '& .MuiSelect-select': { color: palette.text.primary },
  };

  const primaryBtnSx = {
    bgcolor: Colors.primary.main,
    '&:hover': { bgcolor: Colors.primary.dark },
    '&:disabled': { bgcolor: `${Colors.primary.main}50` },
  };

  const isProcessing = uploadPhase === 'validating' || uploadPhase === 'uploading';

  // ── derived summary values ─────────────────────────────────────────────────
  const summary = uploadResult?.summary;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ color: palette.text.primary }}>
        Backup &amp; Data Management
      </Typography>

      {/* ── SECTION: Tenant Onboarding ───────────────────────────────────── */}
      <Card sx={{ ...cardSx, border: `1px solid ${Colors.primary.main}40` }}>
        <CardContent>

          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
            <PeopleAltIcon sx={{ color: Colors.primary.main }} />
            <Typography variant="h6" sx={{ color: palette.text.primary }}>
              Bulk Tenant Onboarding
            </Typography>
            <Chip
              label="One-time import"
              size="small"
              sx={{
                ml: 'auto',
                fontSize: '0.68rem',
                fontWeight: 700,
                bgcolor: `${Colors.primary.main}18`,
                color: Colors.primary.main,
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ color: palette.text.secondary, mb: 3 }}>
            Import existing tenants from an Excel or CSV file. Download the template below,
            fill it in, then upload it for the chosen property.
          </Typography>

          {/* Step 1 — Download template */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              mb: 2,
              borderRadius: 1.5,
              border: `1px dashed ${palette.border}`,
              backgroundColor: palette.background.secondary,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ color: palette.text.primary, fontWeight: 600 }}>
                Step 1 — Download the template
              </Typography>
              <Typography variant="caption" sx={{ color: palette.text.secondary }}>
                Yellow columns are required; green are optional. See the Instructions sheet for details.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={templateLoading ? <CircularProgress size={16} sx={{ color: Colors.primary.main }} /> : <DownloadIcon />}
              onClick={handleTemplateDownload}
              disabled={templateLoading}
              sx={{
                borderColor: Colors.primary.main,
                color: Colors.primary.main,
                flexShrink: 0,
                '&:hover': { borderColor: Colors.primary.dark, backgroundColor: `${Colors.primary.main}10` },
              }}
            >
              {templateLoading ? 'Downloading…' : 'Download Template'}
            </Button>
          </Box>

          {/* Step 2 — Select property */}
          <Typography variant="subtitle2" sx={{ color: palette.text.primary, fontWeight: 600, mb: 1 }}>
            Step 2 — Select property
          </Typography>
          <FormControl variant="outlined" fullWidth margin="none" sx={{ ...selectSx, mb: 2 }}>
            <InputLabel>
              {apartmentsLoading ? 'Loading properties…' : 'Property (Apartment)'}
            </InputLabel>
            <Select
              value={selectedApartmentId}
              onChange={(e) => setSelectedApartmentId(e.target.value)}
              label={apartmentsLoading ? 'Loading properties…' : 'Property (Apartment)'}
              disabled={isProcessing || apartmentsLoading}
              startAdornment={
                apartmentsLoading
                  ? <CircularProgress size={16} sx={{ color: Colors.primary.main, mr: 1 }} />
                  : null
              }
            >
              {!apartmentsLoading && apartments.length === 0 && (
                <MenuItem value="" disabled>
                  No properties found — make sure you have created at least one apartment.
                </MenuItem>
              )}
              {apartments.map((apt) => (
                <MenuItem key={apt.id} value={apt.id}>
                  {apt.name}
                  {apt.location ? (
                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                      — {apt.location}
                    </Typography>
                  ) : null}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Step 3 — Pick & validate file */}
          <Typography variant="subtitle2" sx={{ color: palette.text.primary, fontWeight: 600, mb: 1 }}>
            Step 3 — Choose your file
          </Typography>

          {/* Drop / click zone */}
          <Box
            onClick={() => !isProcessing && fileInputRef.current?.click()}
            sx={{
              border: `2px dashed ${pickedFile ? Colors.primary.main : palette.border}`,
              borderRadius: 2,
              p: 3,
              mb: 2,
              textAlign: 'center',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              transition: 'border-color 0.2s, background-color 0.2s',
              backgroundColor: pickedFile
                ? `${Colors.primary.main}08`
                : palette.background.secondary,
              '&:hover': !isProcessing && {
                borderColor: Colors.primary.main,
                backgroundColor: `${Colors.primary.main}08`,
              },
            }}
          >
            {pickedFile ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                <InsertDriveFileIcon sx={{ color: Colors.primary.main }} />
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" sx={{ color: palette.text.primary, fontWeight: 600 }}>
                    {pickedFile.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: palette.text.secondary }}>
                    {(pickedFile.size / 1024).toFixed(1)} KB — click to replace
                  </Typography>
                </Box>
                {!isProcessing && (
                  <Tooltip title="Remove file">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleReset(); }}
                      sx={{ ml: 'auto', color: palette.text.secondary }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ) : (
              <>
                <UploadFileIcon sx={{ fontSize: 36, color: palette.text.secondary, mb: 0.5 }} />
                <Typography variant="body2" sx={{ color: palette.text.secondary }}>
                  Click to browse for an <strong>.xlsx</strong> or <strong>.csv</strong> file
                </Typography>
              </>
            )}
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={handleFilePick}
          />

          {/* Progress bar while processing */}
          {isProcessing && (
            <LinearProgress
              sx={{
                mb: 2, borderRadius: 1,
                '& .MuiLinearProgress-bar': { backgroundColor: Colors.primary.main },
              }}
            />
          )}

          {/* Error banner */}
          {uploadPhase === 'error' && errorMsg && (
            <Alert
              severity="error"
              onClose={handleReset}
              sx={{ mb: 2, '& .MuiAlert-icon': { color: Colors.accent?.red ?? '#ef4444' } }}
            >
              {errorMsg}
            </Alert>
          )}

          {/* Preview / dry-run result */}
          {uploadPhase === 'preview' && previewData && (
            <Box
              sx={{
                p: 2, mb: 2, borderRadius: 1.5,
                border: `1px solid ${previewData.valid ? `${Colors.primary.main}50` : `${Colors.accent?.red ?? '#ef4444'}50`}`,
                backgroundColor: palette.background.secondary,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {previewData.valid
                  ? <CheckCircleIcon sx={{ color: Colors.primary.main, fontSize: 20 }} />
                  : <WarningAmberIcon sx={{ color: Colors.accent?.orange ?? '#f59e0b', fontSize: 20 }} />
                }
                <Typography variant="subtitle2" sx={{ color: palette.text.primary, fontWeight: 600 }}>
                  {previewData.valid
                    ? `File looks good — ${fmt(previewData.row_count)} tenant rows detected`
                    : 'Column issues found — fix and re-upload'}
                </Typography>
              </Box>

              {/* Column errors */}
              {previewData.column_errors?.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  {previewData.column_errors.map((err, i) => (
                    <Typography key={i} variant="caption" sx={{ display: 'block', color: Colors.accent?.red ?? '#ef4444' }}>
                      • {err}
                    </Typography>
                  ))}
                </Box>
              )}

              {/* Detected columns */}
              {previewData.valid && previewData.mapped_columns && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {Object.entries(previewData.mapped_columns).map(([key, col]) => (
                    <Chip
                      key={key}
                      label={`${key} → "${col}"`}
                      size="small"
                      sx={{
                        fontSize: '0.65rem', fontWeight: 600,
                        bgcolor: `${Colors.primary.main}15`,
                        color: Colors.primary.main,
                        height: 20,
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Final summary after upload */}
          {uploadPhase === 'done' && summary && (
            <Box
              sx={{
                p: 2, mb: 2, borderRadius: 1.5,
                border: `1px solid ${Colors.primary.main}50`,
                backgroundColor: `${Colors.primary.main}08`,
              }}
            >
              <Typography variant="subtitle2" sx={{ color: palette.text.primary, fontWeight: 700, mb: 1.5 }}>
                Import complete
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {[
                  { label: 'Total rows',    value: summary.total,        color: palette.text.primary },
                  { label: 'Tenants added', value: summary.tenants ?? summary.succeeded, color: Colors.primary.main },
                  { label: 'Vacant units',  value: summary.vacant_units ?? 0, color: Colors.accent?.blue ?? '#3b82f6' },
                  { label: 'Failed',        value: summary.failed, color: summary.failed > 0 ? (Colors.accent?.red ?? '#ef4444') : palette.text.secondary },
                ].map((s) => (
                  <Box key={s.label} sx={{ textAlign: 'center', minWidth: 64 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: s.color, lineHeight: 1 }}>
                      {s.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: palette.text.secondary }}>
                      {s.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>

            {/* Validate */}
            {(uploadPhase === 'idle' || uploadPhase === 'error') && (
              <Button
                variant="outlined"
                startIcon={uploadPhase === 'validating' ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                disabled={!pickedFile || !selectedApartmentId || isProcessing}
                onClick={handleValidate}
                sx={{
                  borderColor: Colors.primary.main,
                  color: Colors.primary.main,
                  '&:hover': { borderColor: Colors.primary.dark, backgroundColor: `${Colors.primary.main}10` },
                  '&:disabled': { borderColor: palette.border, color: palette.text.secondary },
                }}
              >
                Validate File
              </Button>
            )}

            {/* Confirm upload (after preview) */}
            {uploadPhase === 'preview' && previewData?.valid && (
              <>
                <Button
                  variant="contained"
                  startIcon={<UploadFileIcon />}
                  onClick={handleUpload}
                  sx={primaryBtnSx}
                >
                  Confirm &amp; Import ({fmt(previewData.row_count)} rows)
                </Button>
                <Button
                  variant="text"
                  onClick={handleReset}
                  sx={{ color: palette.text.secondary }}
                >
                  Cancel
                </Button>
              </>
            )}

            {/* Re-validate if preview had errors */}
            {uploadPhase === 'preview' && !previewData?.valid && (
              <Button variant="outlined" onClick={handleReset} sx={{ borderColor: palette.border, color: palette.text.secondary }}>
                Choose Different File
              </Button>
            )}

            {/* Processing states */}
            {uploadPhase === 'validating' && (
              <Button variant="outlined" disabled sx={{ borderColor: palette.border }}>
                <CircularProgress size={16} sx={{ mr: 1, color: Colors.primary.main }} /> Validating…
              </Button>
            )}
            {uploadPhase === 'uploading' && (
              <Button variant="contained" disabled sx={primaryBtnSx}>
                <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} /> Importing…
              </Button>
            )}

            {/* Done — expand/collapse results or start over */}
            {uploadPhase === 'done' && (
              <>
                <Button
                  variant="outlined"
                  startIcon={showResults ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => setShowResults((p) => !p)}
                  sx={{ borderColor: Colors.primary.main, color: Colors.primary.main }}
                >
                  {showResults ? 'Hide' : 'Show'} Row Results
                </Button>
                <Button variant="text" onClick={handleReset} sx={{ color: palette.text.secondary }}>
                  Import Another File
                </Button>
              </>
            )}
          </Box>

          {/* Scrollable per-row results */}
          <Collapse in={showResults && uploadPhase === 'done'}>
            <Divider sx={{ my: 2, borderColor: palette.border }} />
            <Typography variant="subtitle2" sx={{ color: palette.text.secondary, mb: 1 }}>
              Per-row breakdown
            </Typography>
            <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
              {uploadResult?.results?.map((r) => (
                <ResultRow key={r.row} result={r} palette={palette} Colors={Colors} />
              ))}
            </Box>
          </Collapse>

        </CardContent>
      </Card>

      {/* ── SECTION: Automatic Backups ────────────────────────────────────── */}
      <Card sx={cardSx}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: palette.text.primary }}>
            Automatic Backups
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={backupSettings.autoBackup}
                onChange={handleSettingsChange('autoBackup')}
                sx={switchSx}
              />
            }
            label={<Typography sx={{ color: palette.text.primary }}>Enable Automatic Backups</Typography>}
          />
          <Typography variant="body2" sx={{ mt: 1, mb: 2, color: palette.text.secondary }}>
            System will automatically create backups of your data weekly
          </Typography>

          <FormControl variant="outlined" fullWidth margin="normal" sx={selectSx}>
            <InputLabel>Data Retention Period</InputLabel>
            <Select
              value={backupSettings.dataRetention}
              onChange={handleSettingsChange('dataRetention')}
              label="Data Retention Period"
            >
              <MenuItem value="1 year">1 Year</MenuItem>
              <MenuItem value="3 years">3 Years</MenuItem>
              <MenuItem value="5 years">5 Years</MenuItem>
              <MenuItem value="7 years">7 Years</MenuItem>
              <MenuItem value="Forever">Forever</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* ── SECTION: Manual Backup ────────────────────────────────────────── */}
      <Card sx={cardSx}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <StorageIcon sx={{ color: Colors.primary.main }} />
            <Typography variant="h6" sx={{ color: palette.text.primary }}>Manual Backup</Typography>
          </Box>
          <Typography variant="body2" gutterBottom sx={{ color: palette.text.secondary }}>
            Create a backup of all your system data right now
          </Typography>
          <Button
            variant="contained"
            startIcon={<BackupIcon />}
            sx={{ mt: 1, ...primaryBtnSx }}
            onClick={handleCreateBackup}
          >
            Create Backup
          </Button>
        </CardContent>
      </Card>

      {/* ── SECTION: Previous Backups ─────────────────────────────────────── */}
      <Card sx={{ ...cardSx, mb: 0 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: palette.text.primary }}>
            Previous Backups
          </Typography>
          <List disablePadding>
            {backups.map((backup, index) => (
              <ListItem
                key={backup.id}
                divider={index < backups.length - 1}
                sx={{
                  px: 2, py: 1.5, borderRadius: 1, mb: 0.5,
                  backgroundColor: palette.background.secondary,
                  '& .MuiDivider-root': { borderColor: palette.border },
                  transition: 'background-color 0.2s',
                  '&:hover': { backgroundColor: `${Colors.primary.main}10` },
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.text.primary }}>
                      {backup.date}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip
                        label={backup.type}
                        size="small"
                        sx={{
                          bgcolor: backup.type === 'Manual backup'
                            ? `${Colors.accent?.blue ?? '#3b82f6'}20`
                            : `${Colors.primary.main}20`,
                          color: backup.type === 'Manual backup'
                            ? (Colors.accent?.blue ?? '#3b82f6')
                            : Colors.primary.main,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                      <Typography variant="caption" sx={{ color: palette.text.muted }}>{backup.size}</Typography>
                    </Box>
                  }
                />
                <Button
                  startIcon={<CloudDownloadIcon />}
                  onClick={() => handleRestoreBackup(backup.id)}
                  sx={{ color: Colors.primary.main, minWidth: 'auto' }}
                >
                  Restore
                </Button>
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BackupSettings;